import React, { useState } from "react";
import "./hallDetailInfoSection.scss";
import {
  IndividualHallRegularPrice,
  IndividualHallStyle,
  IndividualHallVirginRoad,
} from "@src/types/weddingHall";
import { addComma } from "@src/utils";
import IconInfoCircle from "@assets/images/common/community/icon-info-circle@3x.png";
import { TooltipB } from "@src/shared/src";

type Props = {
  weddingGapMinutes: number[];
  weddingTime: string;
  seatedCapacity: number;
  capacity: number;
  guaranteedNumberOfPeople: number;
  style: IndividualHallStyle;
  height: number;
  virginRoad: IndividualHallVirginRoad;
  regularPrice: IndividualHallRegularPrice;
};

const Divider = () => {
  return <div style={{ height: 1, backgroundColor: "#EAEAEA" }}></div>;
};
const getLightingConditionStr = (lightingCondition: string) => {
  let value = "";
  switch (lightingCondition) {
    case "DARK":
      value = "어두운";
      break;
    case "BRIGHT":
      value = "밝은";
      break;
  }
  return value;
};
const getPrimaryTypeOrSecondaryTypeStr = (primaryType: string) => {
  let value = "";
  switch (primaryType) {
    case "CHAPEL":
      value = "채플";
      break;
    case "CONVENTION":
      value = "컨벤션";
      break;
    case "HOUSE":
      value = "하우스";
      break;
    case "HOTEL":
      value = "호텔";
      break;
    case "OUTDOOR":
      value = "야외";
      break;
    case "HANOK":
      value = "한옥";
      break;
    case "SMALL_SCALE":
      value = "소규모";
      break;
    case null:
      value = "";
      break;
  }
  return value;
};
const getVirginRoadHeightStr = (virginRoadHeight: string) => {
  let value = "";
  switch (virginRoadHeight) {
    case "HIGH":
      value = "있음(높음)";
      break;
    case "LOW":
      value = "있음(낮음)";
      break;
    case "NONE":
      value = "없음";
      break;
  }
  return value;
};

export default function HallDetailInfoSection({
  weddingGapMinutes,
  weddingTime,
  seatedCapacity,
  capacity,
  guaranteedNumberOfPeople,
  style,
  height,
  virginRoad,
  regularPrice,
}: Props) {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <section className="detail-section-container">
      <h2>홀 세부 정보</h2>
      <ul className="info-wrap">
        <li className="title-content-wrap">
          <p className="title">예식간격</p>
          <p>
            {weddingGapMinutes.map((minute, i) => (
              <span key={i}>
                {minute}분{weddingGapMinutes.length - 1 === i ? "" : ", "}
              </span>
            ))}
          </p>
        </li>
        <li className="title-content-wrap">
          <p className="title">예식시간</p>
          <p>{weddingTime}</p>
        </li>
        <li className="title-content-wrap">
          <p className="title">보증인원</p>
          <p>추천 {guaranteedNumberOfPeople}명</p>
        </li>
        <li className="title-content-wrap">
          <p className="title">수용인원</p>
          <p>
            착석 {seatedCapacity}석 / 수용 {capacity}명{" "}
            <img
              src={IconInfoCircle}
              alt="info"
              width={16}
              height={16}
              onClick={() => setShowTooltip(true)}
              style={{ verticalAlign: "sub" }}
            />
            <TooltipB
              isShow={showTooltip}
              title="착석인원/수용인원"
              close={() => setShowTooltip(false)}
              direction="bottom"
              pointerLocation="center"
              widthValue={{ width: "calc(100vw - 40px)", maxWidth: "90vw", minWidth: "230px" }}
              marginForCustom="0px -20% 0px 0px"
              extra={
                <p style={{ whiteSpace: "pre-line", color: "#737373", fontSize: "12px" }}>
                  {`착석 : 예식홀 내부 좌석에 앉을 수 있는 최대 인원 수
                수용 : 예식홀 내부에 수용할 수 있는 최대 인원 수
                
                *일부 하객은 예식을 보지 않고 바로 식사만 하는 경우도 있어 추천 보증인원과 실제 착석 인원은 차이가 날 수 있습니다.`}
                </p>
              }
            />
          </p>
        </li>
      </ul>
      <Divider />
      <ul className="info-wrap">
        <li className="title-content-wrap">
          <p className="title">스타일</p>
          <p>
            {getLightingConditionStr(style.lightingCondition)} /{" "}
            {getPrimaryTypeOrSecondaryTypeStr(style.primaryType)}{" "}
            {style.secondaryType !== null && "/ "}
            {getPrimaryTypeOrSecondaryTypeStr(style.secondaryType)}
          </p>
        </li>
        <li className="title-content-wrap">
          <p className="title">천고</p>
          <p>{height}m</p>
        </li>
        <li className="title-content-wrap">
          <p className="title">버진로드</p>
          <p>{virginRoad.length}m</p>
        </li>
        <li className="title-content-wrap">
          <p className="title">단상</p>
          <p>{getVirginRoadHeightStr(virginRoad.height)}</p>
        </li>
      </ul>
      <Divider />
      <ul className="info-wrap">
        <li className="title-content-wrap">
          <p className="title">대관료</p>
          <p>{addComma(regularPrice.rentalFee)}원</p>
        </li>
        <li className="title-content-wrap">
          <p className="title">플라워(필수)</p>
          <p>{regularPrice.flowerFee === 0 ? "포함" : `${addComma(regularPrice.flowerFee)}원`}</p>
        </li>
        <li className="title-content-wrap">
          <p className="title">식대(대인)</p>
          <p>{addComma(regularPrice.adultDrinkFee)}원</p>
        </li>
        <li className="title-content-wrap">
          <p className="title">식대(소인)</p>
          <p>{addComma(regularPrice.childDrinkFee)}원</p>
        </li>
        <li className="title-content-wrap">
          <p className="title">음료</p>
          <p>{regularPrice.includeDrinkFee ? "포함" : "당일 소모량에 따라 발생"}</p>
        </li>
        <li className="title-content-wrap">
          <p className="title">주류</p>
          <p>{regularPrice.includeAlcoholFee ? "포함" : "당일 소모량에 따라 발생"}</p>
        </li>
        <li className="title-content-wrap">
          <p className="title">봉사료</p>
          <p>{regularPrice.includeServiceFee ? "포함" : "불포함"}</p>
        </li>
        <li className="title-content-wrap">
          <p className="title">부가세</p>
          <p>{regularPrice.includeVat ? "포함" : "불포함"}</p>
        </li>
        {regularPrice.additionalProducts.map(({ name, price, quantity, required }, i) => (
          <li className="title-content-wrap" key={i}>
            <p className="title">
              {name}
              {required ? "(필수)" : ""}
            </p>
            <p>{addComma(price * quantity)}원</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
